import { FINANCIAL_FORECAST_REDUCER_KEY } from "../index";
import { getUnitSymbolByUnit, itemType, unitType } from "../types";
import { years } from "store/utils/years";
import { defaultZeroMonths } from "./staticMonthsData";
import { GeneralCoType } from "constants/companyTypes";
import * as actionTypes from "../../actions/actionTypes";

export const BILLABLE_HOURS_REDUCER_KEY = "billableHours";
export const HOURS_KEY = "hours";
export const HOURLY_RATE_KEY = "hourlyRate";
export const INCOME_FROM_SALES_KEY = "incomeFromSales";
export const COST_PRICE = "costPrice";
export const COST_OF_SALES = "costOfSales";
export const BILLABLE_HOURS_INSTANCES_KEY = "billableHoursInstances";
export const BILLABLE_HOURS_TOTAL_INCOME_KEY = "billableHoursTotalIncome";
export const TOTAL_COST_OF_BILLABLE_HOURS_KEY = "totalCostOfBillableHours";

export const billableHoursInstanceItems = [
  HOURS_KEY,
  HOURLY_RATE_KEY,
  INCOME_FROM_SALES_KEY,
  COST_PRICE,
  COST_OF_SALES,
];

export const billableHoursTotalIncomeItems = [
  BILLABLE_HOURS_TOTAL_INCOME_KEY,
  TOTAL_COST_OF_BILLABLE_HOURS_KEY,
];

const shouldUseMonthly = (getReducerFunc) => {
  if (!getReducerFunc) {
    return false;
  }
  try {
    const forecastState = getReducerFunc(FINANCIAL_FORECAST_REDUCER_KEY);
    return (
      forecastState?.timeSeriesAssumptions?.primaryPeriodicity === "Monthly"
    );
  } catch (err) {
    return false;
  }
};

const getPeriodValue = (item, key, getReducerFunc) => {
  if (!item) {
    return 0;
  }
  if (shouldUseMonthly(getReducerFunc)) {
    const monthKey = item.months?.[key] ?? item.months?.[String(key)];
    if (monthKey !== undefined) {
      return Number(monthKey) || 0;
    }
  }
  return Number(item.years?.[key] || 0);
};

export const billableHoursInstanceState = {
  [HOURS_KEY]: {
    title: "Hours",
    unit: unitType.NUMBER,
    unitSymbol: getUnitSymbolByUnit(unitType.NUMBER),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [GeneralCoType],
    allowDecimals: false,
    stripLeadingZeros: true,
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return 0;
    },
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [HOURLY_RATE_KEY]: {
    title: "Hourly Rate",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.NUMBER),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return 0;
    },
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [INCOME_FROM_SALES_KEY]: {
    title: "Income from Billable Hours",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      if (!instanceId) {
        return 0;
      }

      const instance = getReducerFunc(BILLABLE_HOURS_REDUCER_KEY)[
        BILLABLE_HOURS_INSTANCES_KEY
      ][instanceId];
      const hours = getPeriodValue(
        instance[HOURS_KEY],
        yearKey,
        getReducerFunc
      );
      const hourlyRate = getPeriodValue(
        instance[HOURLY_RATE_KEY],
        yearKey,
        getReducerFunc
      );
      return hours * hourlyRate;
    },
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [COST_PRICE]: {
    title: "Cost Price",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.NUMBER),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return 0;
    },
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [COST_OF_SALES]: {
    title: "Cost of Sales",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.NUMBER),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      if (!instanceId) {
        return 0;
      }

      const instance = getReducerFunc(BILLABLE_HOURS_REDUCER_KEY)[
        BILLABLE_HOURS_INSTANCES_KEY
      ][instanceId];
      const costPrice = getPeriodValue(
        instance[COST_PRICE],
        yearKey,
        getReducerFunc
      );
      const hours = getPeriodValue(
        instance[HOURS_KEY],
        yearKey,
        getReducerFunc
      );
      return costPrice * hours;
    },
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
};

export const initialState = {
  [BILLABLE_HOURS_INSTANCES_KEY]: {},
  [BILLABLE_HOURS_TOTAL_INCOME_KEY]: {
    title: "Income from Billable Hours",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.NUMBER),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const billableHoursState = getReducerFunc(BILLABLE_HOURS_REDUCER_KEY);
      const instances = billableHoursState[BILLABLE_HOURS_INSTANCES_KEY];

      let total = 0;
      Object.values(instances).forEach((instanceData) => {
        const hours = getPeriodValue(
          instanceData[HOURS_KEY],
          yearKey,
          getReducerFunc
        );
        const hourlyRate = getPeriodValue(
          instanceData[HOURLY_RATE_KEY],
          yearKey,
          getReducerFunc
        );
        total += hours * hourlyRate;
      });

      return total;
    },
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [TOTAL_COST_OF_BILLABLE_HOURS_KEY]: {
    title: "Cost of Billable Hours",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.NUMBER),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      const billableHoursState = getReducerFunc(BILLABLE_HOURS_REDUCER_KEY);
      const instances = billableHoursState[BILLABLE_HOURS_INSTANCES_KEY];

      let total = 0;
      Object.values(instances).forEach((instanceData) => {
        const costPrice = getPeriodValue(
          instanceData[COST_PRICE],
          yearKey,
          getReducerFunc
        );
        const hours = getPeriodValue(
          instanceData[HOURS_KEY],
          yearKey,
          getReducerFunc
        );
        total += costPrice * hours;
      });

      return total;
    },
    months: defaultZeroMonths,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
};

export const defaultBillableHoursState = {
  ...billableHoursInstanceState,
};

const billableHoursReducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.ADD_BILLABLE_HOURS_INSTANCE:
      return {
        ...state,
        [BILLABLE_HOURS_INSTANCES_KEY]: {
          ...state[BILLABLE_HOURS_INSTANCES_KEY],
          [action.payload.id]: { ...defaultBillableHoursState },
        },
      };
    case actionTypes.UPDATE_BILLABLE_HOURS_INSTANCE:
      return {
        ...state,
        [BILLABLE_HOURS_INSTANCES_KEY]: {
          ...state[BILLABLE_HOURS_INSTANCES_KEY],
          [action.payload.id]: {
            ...state[BILLABLE_HOURS_INSTANCES_KEY][action.payload.id],
            ...action.payload.data,
          },
        },
      };
    case actionTypes.REMOVE_BILLABLE_HOURS_INSTANCE: {
      const { [action.payload.id]: removed, ...remaining } =
        state[BILLABLE_HOURS_INSTANCES_KEY];
      return {
        ...state,
        [BILLABLE_HOURS_INSTANCES_KEY]: remaining,
      };
    }
    case actionTypes.SET_BILLABLE_HOURS_INSTANCE_BY_MONTH: {
      const instance = state[BILLABLE_HOURS_INSTANCES_KEY][action.id];
      const item = instance[action.itemKey];
      return {
        ...state,
        [BILLABLE_HOURS_INSTANCES_KEY]: {
          ...state[BILLABLE_HOURS_INSTANCES_KEY],
          [action.id]: {
            ...instance,
            [action.itemKey]: {
              ...item,
              months: {
                ...item.months,
                [action.monthKey]: action.amount,
              },
            },
          },
        },
      };
    }
    case actionTypes.REPLACE_BILLABLE_HOURS_INSTANCE_MONTHS: {
      const instance = state[BILLABLE_HOURS_INSTANCES_KEY][action.id];
      return {
        ...state,
        [BILLABLE_HOURS_INSTANCES_KEY]: {
          ...state[BILLABLE_HOURS_INSTANCES_KEY],
          [action.id]: {
            ...instance,
            [action.itemKey]: {
              ...instance[action.itemKey],
              months: action.payload,
            },
          },
        },
      };
    }
    case actionTypes.RESET_BILLABLE_HOURS_REDUCER:
      return initialState;
    default:
      return state;
  }
};

export default billableHoursReducer;
